import { areDisplayCreativesComplete } from '@features/campaigns/shared/config/adProductsCountryConfig.helpers.ts';
import { getCampaignAdProductsCountryConfig } from '@features/campaigns/shared/config/adProductsCountryConfig.ts';
import {
  AdProductVideoCreativeStatus,
  CampaignType,
  CampaignTypes,
} from '@features/campaigns/shared/types/campaign.ts';
import { DataStatuses } from '@shared/types/common.ts';
import { UserCountryCode } from '@shared/types/countryCodes.ts';

interface CampaignProps {
  type?: CampaignType;
  isDisplayAdProductSelected: boolean;
  isVideoAdProductSelected: boolean;
  videoSpotStatus?: AdProductVideoCreativeStatus;
  lShapeImageUrl?: string | null;
  landingImageUrl?: string | null;
  switchInLeftImageUrl?: string | null;
  switchInBottomImageUrl?: string | null;
  videoSpotUrl?: string | null;
  advertiserCode?: string;
  billingContactCode?: string;
}

interface SubmitButtonDisabled {
  campaignProps: CampaignProps;
  isInvalidSectionsEmpty: boolean;
  isExternalAgency: boolean;
  isExternalDirectClient: boolean;
  isOnboardingCompleted: boolean;
  countryCode: UserCountryCode;
}

export function useSubmitButtonValidation() {
  function determineIfShouldDisableSubmit(params: SubmitButtonDisabled) {
    const {
      campaignProps,
      isInvalidSectionsEmpty,
      isExternalAgency,
      isExternalDirectClient,
      isOnboardingCompleted,
      countryCode,
    } = params;
    const {
      isDisplayAdProductSelected,
      isVideoAdProductSelected,
      lShapeImageUrl,
      landingImageUrl,
      switchInLeftImageUrl,
      switchInBottomImageUrl,
      videoSpotUrl,
      videoSpotStatus,
      type,
      billingContactCode,
      advertiserCode,
    } = campaignProps;

    const isCampaignSelfService = type === CampaignTypes.SELF_SERVICE;
    const countryConfig = getCampaignAdProductsCountryConfig(countryCode);
    const areDisplayCreativesUploaded = areDisplayCreativesComplete(countryConfig, {
      lShapeImageUrl,
      landingImageUrl,
      switchInLeftImageUrl,
      switchInBottomImageUrl,
    });
    const isVideoCreativeUploaded = Boolean(videoSpotUrl);
    const isVideoCreativeNotRejected = videoSpotStatus !== DataStatuses.REJECTED;
    const isAdvertiserAndBillingContactAddedForExternalCampaign =
      isCampaignSelfService && Boolean(advertiserCode) && Boolean(billingContactCode);
    const isCurrentUserExternal = isExternalDirectClient || isExternalAgency;

    function determineIfRequiredCreativesAreUploaded() {
      if (!isCampaignSelfService) {
        return false;
      }

      if (isDisplayAdProductSelected && isVideoAdProductSelected) {
        return areDisplayCreativesUploaded && isVideoCreativeUploaded && isVideoCreativeNotRejected;
      }

      if (isDisplayAdProductSelected) {
        return areDisplayCreativesUploaded;
      }

      if (isVideoAdProductSelected) {
        return isVideoCreativeUploaded && isVideoCreativeNotRejected;
      }

      return false;
    }

    function determineIfUserOnboardingCompleted() {
      if (!isCampaignSelfService) {
        return true;
      }

      if (isCurrentUserExternal) {
        return isOnboardingCompleted;
      }

      return true;
    }

    const areRequiredCreativesUploadedForExternalCampaign = determineIfRequiredCreativesAreUploaded();
    const isExternalUserOnboardingCompleted = determineIfUserOnboardingCompleted();

    const campaignTypeSpecificConditionsAreMet = isCampaignSelfService
      ? areRequiredCreativesUploadedForExternalCampaign && isAdvertiserAndBillingContactAddedForExternalCampaign
      : true;

    return !isInvalidSectionsEmpty || !campaignTypeSpecificConditionsAreMet || !isExternalUserOnboardingCompleted;
  }

  return {
    determineIfShouldDisableSubmit,
  };
}
